import React, { useState } from 'react';
import { Clock, DollarSign, TrendingUp, Shield, Megaphone, Headset, Apple, Smartphone, Sparkles, CheckCircle, AlertCircle } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { mockData } from '../mock';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const iconMap = {
  'clock': Clock,
  'dollar-sign': DollarSign,
  'trending-up': TrendingUp,
  'shield': Shield,
  'megaphone': Megaphone,
  'headset': Headset
};

const VendorSignup = () => {
  const [formData, setFormData] = useState({
    businessName: '',
    ownerName: '',
    email: '',
    phone: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      // For now, we'll show success message - you can add backend endpoint later
      setSubmitStatus('success');
      setFormData({ businessName: '', ownerName: '', email: '', phone: '' });
      
      setTimeout(() => {
        setSubmitStatus(null);
      }, 5000);
    } catch (error) {
      console.error('Error submitting vendor registration:', error);
      setSubmitStatus('error');
      
      setTimeout(() => {
        setSubmitStatus(null);
      }, 5000);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <section id="vendor-signup" className="relative py-20 overflow-hidden bg-gradient-to-br from-yellow-400 via-yellow-300 to-yellow-500">
      {/* Decorative Elements */}
      <div className="absolute top-0 left-0 w-64 h-64 bg-black/5 rounded-full -translate-x-32 -translate-y-32"></div>
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-black/5 rounded-full translate-x-48 translate-y-48"></div>
      
      {/* Giant Cute Bee */}
      <div className="giant-bee">
        <div className="giant-bee-body">
          <div className="giant-bee-stripe"></div>
          <div className="giant-bee-stripe"></div>
          <div className="giant-bee-stripe"></div>
          <div className="giant-bee-face">
            <div className="giant-bee-eye left"></div>
            <div className="giant-bee-eye right"></div>
            <div className="giant-bee-smile"></div>
          </div>
        </div>
        <div className="giant-bee-wings">
          <div className="giant-bee-wing left"></div>
          <div className="giant-bee-wing right"></div>
        </div>
      </div>
      
      <div className="container mx-auto px-4 relative z-10">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="flex items-center justify-center gap-3 mb-4">
            <Sparkles className="text-black" size={32} />
            <h2 className="text-4xl md:text-5xl font-bold text-black">
              Become a Busy Bee
            </h2>
            <Sparkles className="text-black" size={32} />
          </div>
          <p className="text-black/80 text-lg md:text-xl max-w-3xl mx-auto">
            Join thousands of service providers earning income on their own terms. 
            Create your Hyyve and start buzzing with business today!
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 max-w-6xl mx-auto">
          {/* Benefits Section */}
          <div>
            <h3 className="text-2xl font-bold text-black mb-6">Why Join bHyyve?</h3>
            <div className="space-y-4">
              {mockData.vendorBenefits.map((benefit) => {
                const Icon = iconMap[benefit.icon];
                return (
                  <div
                    key={benefit.id}
                    className="bg-white/90 backdrop-blur-sm p-5 rounded-xl border-2 border-black/10 hover:border-black/30 transition-all duration-300 hover:shadow-lg"
                  >
                    <div className="flex items-start gap-4">
                      <div className="bg-black p-3 rounded-lg flex-shrink-0">
                        <Icon className="text-yellow-400" size={24} />
                      </div>
                      <div>
                        <h4 className="font-bold text-black text-lg mb-1">{benefit.title}</h4>
                        <p className="text-black/70 text-sm">{benefit.description}</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Vendor App Downloads */}
            <div className="mt-8 bg-black/90 backdrop-blur-sm p-6 rounded-2xl">
              <h4 className="text-xl font-bold text-yellow-400 mb-4">Download Vendor App</h4>
              <p className="text-white/80 text-sm mb-4">
                Manage your Hyyve on the go with our powerful vendor app
              </p>
              <div className="grid grid-cols-2 gap-4">
                <a
                  href={mockData.appLinks.vendorIos}
                  className="bg-yellow-400 hover:bg-yellow-500 text-black font-semibold py-3 px-4 rounded-lg flex items-center justify-center gap-2 transition-all duration-200"
                >
                  <Apple size={20} />
                  <span className="text-sm">iOS App</span>
                </a>
                <a
                  href={mockData.appLinks.vendorAndroid}
                  className="bg-yellow-400 hover:bg-yellow-500 text-black font-semibold py-3 px-4 rounded-lg flex items-center justify-center gap-2 transition-all duration-200"
                >
                  <Smartphone size={20} />
                  <span className="text-sm">Android App</span>
                </a>
              </div>
            </div>
          </div>

          {/* Registration Form */}
          <div>
            <div className="bg-white/95 backdrop-blur-sm p-8 rounded-2xl border-2 border-black/10 shadow-2xl">
              <h3 className="text-2xl font-bold text-black mb-2">Start Your Hyyve</h3>
              <p className="text-black/70 mb-6">Fill out the form below and we'll get you buzzing in no time!</p>

              {/* Success Message */}
              {submitStatus === 'success' && (
                <div className="bg-green-50 border-2 border-green-400 rounded-lg p-4 flex items-start gap-3 mb-6">
                  <CheckCircle className="text-green-600 flex-shrink-0" size={24} />
                  <div>
                    <p className="text-green-800 font-semibold">Registration Received!</p>
                    <p className="text-green-700 text-sm">We'll contact you within 24 hours to set up your Hyyve.</p>
                  </div>
                </div>
              )}

              {/* Error Message */}
              {submitStatus === 'error' && (
                <div className="bg-red-50 border-2 border-red-400 rounded-lg p-4 flex items-start gap-3 mb-6">
                  <AlertCircle className="text-red-600 flex-shrink-0" size={24} />
                  <div>
                    <p className="text-red-800 font-semibold">Registration Failed</p>
                    <p className="text-red-700 text-sm">Please try again or contact us at support@bhyyve.com</p>
                  </div>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <label htmlFor="businessName" className="block text-sm font-semibold text-black mb-2">
                    Business Name *
                  </label>
                  <Input
                    id="businessName"
                    name="businessName"
                    type="text"
                    placeholder="Your Business Name"
                    value={formData.businessName}
                    onChange={handleChange}
                    required
                    disabled={isSubmitting}
                    className="w-full"
                  />
                </div>

                <div>
                  <label htmlFor="ownerName" className="block text-sm font-semibold text-black mb-2">
                    Owner Name *
                  </label>
                  <Input
                    id="ownerName"
                    name="ownerName"
                    type="text"
                    placeholder="Your Full Name"
                    value={formData.ownerName}
                    onChange={handleChange}
                    required
                    disabled={isSubmitting}
                    className="w-full"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-semibold text-black mb-2">
                    Email Address *
                  </label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    placeholder="your@email.com"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    disabled={isSubmitting}
                    className="w-full"
                  />
                </div>

                <div>
                  <label htmlFor="phone" className="block text-sm font-semibold text-black mb-2">
                    Phone Number *
                  </label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    placeholder="+1 (555) 123-4567"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    disabled={isSubmitting}
                    className="w-full"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-black text-yellow-400 hover:bg-gray-900 font-bold py-6 text-lg disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-yellow-400 mr-2"></div>
                      Submitting...
                    </>
                  ) : (
                    <>
                      Create My Hyyve
                    </>
                  )}
                </Button>

                <p className="text-xs text-black/60 text-center">
                  By submitting, you agree to our Terms of Service and Privacy Policy
                </p>
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default VendorSignup;
